import { Dispatch, FC, SetStateAction, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { Autocomplete, IconButton, Stack, TextField, Tooltip } from '@mui/material';

import { useQuery } from '@tanstack/react-query';

import { MediaPlanResponse, PageResponseMediaPlanResponse } from '@/api/data-contracts';
import { useMediaPlansService } from '@/features/media-plans';
import { queryKeys } from '@/react-query/constants';
import { UserTypeContext } from '@/shared/lib/UserTypeContext/UserTypeContext';

const ShoppingCartMediaPlanPickerPlanInput: FC<{
  selectedMediaPlan: MediaPlanResponse | null;
  setSelectedMediaPlan: Dispatch<SetStateAction<MediaPlanResponse | null>>;
  setActiveStep: Dispatch<SetStateAction<number>>;
}> = ({ selectedMediaPlan, setSelectedMediaPlan, setActiveStep }) => {
  const { t } = useTranslation();
  const userType = useContext(UserTypeContext);
  const { getMediaPlans } = useMediaPlansService();

  const { data: mediaPlans } = useQuery<PageResponseMediaPlanResponse>(
    [queryKeys.mediaPlans],
    ({ signal }) => getMediaPlans({ request: { pageNumber: 0, numberOfItems: 1000 } }, signal),
    { enabled: userType === 'advertiser' },
  );

  return (
    <Stack direction="row" spacing={0.5} sx={{ width: '100%' }}>
      <Autocomplete
        fullWidth
        options={mediaPlans?.content ?? []}
        getOptionLabel={(option: MediaPlanResponse) => option.planName}
        onChange={(_, plan) => setSelectedMediaPlan(plan)}
        value={selectedMediaPlan}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('Select Media Plan')}
            sx={{
              minWidth: '20rem',
              '& .MuiFormLabel-root': {
                top: '-0.4rem',
              },
            }}
          />
        )}
      />

      <Tooltip title={t('Create new Media Plan')}>
        <IconButton
          //   @ts-ignore
          color="accent"
          onClick={() => {
            setActiveStep(2);
          }}
          data-testid="create-new-media-plan-button"
        >
          <AddOutlinedIcon />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

export default ShoppingCartMediaPlanPickerPlanInput;
